(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-prohealth-popup-root]';
  const POPUP_SELECTOR = '[data-prohealth-popup]';
  const CART_TOTAL_KEY = 'prohealthPromoPopup:lastCartTotal';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/;
  const CART_SETTLE_MS = 350;

  const collectionProductCache = new Map();
  const registered = [];
  let activePopup = null;
  let previousFocus = null;
  let cartTotal = null;

  const splitList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

  function safeSessionGet(key) {
    try { return sessionStorage.getItem(key); } catch (error) { return null; }
  }

  function safeSessionSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (error) {}
  }

  function storageKey(popup) {
    return `prohealthPromoPopup:${popup.dataset.campaignId}`;
  }

  function frequencyAllows(popup, designMode) {
    if (designMode) return true;
    const frequency = popup.dataset.frequency || 'session';
    const key = storageKey(popup);
    if (frequency === 'always') return true;
    try {
      if (frequency === 'session') return sessionStorage.getItem(key) !== 'shown';
      const lastShown = Number(localStorage.getItem(key) || 0);
      if (!lastShown) return true;
      const day = 24 * 60 * 60 * 1000;
      const elapsed = Date.now() - lastShown;
      return frequency === 'day' ? elapsed >= day : elapsed >= day * 7;
    } catch (error) {
      return true;
    }
  }

  function markShown(popup, designMode) {
    if (designMode) return;
    const frequency = popup.dataset.frequency || 'session';
    const key = storageKey(popup);
    try {
      if (frequency === 'session') sessionStorage.setItem(key, 'shown');
      if (frequency === 'day' || frequency === 'week') localStorage.setItem(key, String(Date.now()));
    } catch (error) {}
  }

  /* ---------- Cart value range ---------- */

  function readCents(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function hasCartRange(popup) {
    return readCents(popup.dataset.cartMin) !== null || readCents(popup.dataset.cartMax) !== null;
  }

  // Min is inclusive ("show from €X"), max is exclusive ("stop at €Y").
  function cartInRange(popup, total) {
    if (total === null || total === undefined) return false;
    const min = readCents(popup.dataset.cartMin);
    const max = readCents(popup.dataset.cartMax);
    if (min !== null && total < min) return false;
    if (max !== null && total >= max) return false;
    return true;
  }

  function needsCartTotal() {
    return registered.some(({ popup }) => popup.dataset.enabled === 'true' && (popup.dataset.trigger === 'cart_value' || hasCartRange(popup)));
  }

  function setCartTotal(total) {
    cartTotal = total;
    safeSessionSet(CART_TOTAL_KEY, String(total));
  }

  async function fetchCartTotal() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      return typeof cart.total_price === 'number' ? cart.total_price : null;
    } catch (error) {
      return null;
    }
  }

  /* ---------- Open / close ---------- */

  function openPopup(popup, designMode) {
    if (!popup || activePopup === popup || popup.dataset.enabled !== 'true') return;
    if (!frequencyAllows(popup, designMode)) return;
    // Re-check at open time: the cart may have changed during the delay.
    if (hasCartRange(popup) && !cartInRange(popup, cartTotal)) return;
    if (activePopup) closePopup(activePopup);

    previousFocus = document.activeElement;
    popup.classList.add('is-open');
    document.body.classList.add('prohealth-promo-popup-open');
    activePopup = popup;
    markShown(popup, designMode);

    const dialog = popup.querySelector('.prohealth-promo-popup__dialog');
    if (dialog) requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  function closePopup(popup) {
    if (!popup) return;
    popup.classList.remove('is-open');
    if (activePopup === popup) activePopup = null;
    document.body.classList.remove('prohealth-promo-popup-open');
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus({ preventScroll: true });
    previousFocus = null;
  }

  function scheduleOpen(popup, designMode) {
    const delay = Math.max(0, Number(popup.dataset.delay || 0)) * 1000;
    window.setTimeout(() => openPopup(popup, designMode), delay);
  }

  function isCandidate(entry) {
    const { popup, designMode } = entry;
    if (popup.dataset.enabled !== 'true' || !frequencyAllows(popup, designMode)) return false;
    return !hasCartRange(popup) || cartInRange(popup, cartTotal);
  }

  /* ---------- Product / collection targeting ---------- */

  function popupHasVisitTarget(popup, context) {
    const productIds = splitList(popup.dataset.targetProductIds);
    const collectionHandles = splitList(popup.dataset.targetCollectionHandles);
    if (context.productId && productIds.includes(String(context.productId))) return true;
    if (context.collectionHandle && collectionHandles.includes(context.collectionHandle)) return true;
    return context.productCollections.some((handle) => collectionHandles.includes(handle));
  }

  async function collectionContainsProduct(collectionHandle, productId) {
    if (!collectionHandle || !productId) return false;
    if (!collectionProductCache.has(collectionHandle)) {
      collectionProductCache.set(collectionHandle, (async () => {
        try {
          const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) return new Set();
          const data = await response.json();
          return new Set((data.products || []).map((product) => String(product.id)));
        } catch (error) {
          return new Set();
        }
      })());
    }
    const productIds = await collectionProductCache.get(collectionHandle);
    return productIds.has(String(productId));
  }

  async function popupMatchesAddedProduct(popup, productId) {
    if (!productId) return false;
    const productIds = splitList(popup.dataset.targetProductIds);
    if (productIds.includes(String(productId))) return true;
    const collectionHandles = splitList(popup.dataset.targetCollectionHandles);
    for (const handle of collectionHandles) {
      if (await collectionContainsProduct(handle, productId)) return true;
    }
    return false;
  }

  /* ---------- Cart changes ---------- */

  // Cart changes arrive in bursts (customer add, then the gift scripts adjusting
  // the cart). Wait for the burst to settle, then evaluate once with the final total.
  const pending = { timer: null, productIds: [], total: null };

  function queueCartMutation(productId, total) {
    if (productId) pending.productIds.push(String(productId));
    pending.total = typeof total === 'number' ? total : null;
    window.clearTimeout(pending.timer);
    pending.timer = window.setTimeout(flushCartMutations, CART_SETTLE_MS);
  }

  async function flushCartMutations() {
    const productIds = pending.productIds.splice(0);
    const knownTotal = pending.total;
    pending.total = null;

    // Only read the cart when a campaign depends on its value.
    const previousTotal = cartTotal;
    if (needsCartTotal()) {
      const total = knownTotal !== null ? knownTotal : await fetchCartTotal();
      if (total !== null) setCartTotal(total);
    } else if (knownTotal !== null) {
      setCartTotal(knownTotal);
    }

    // 1. Add-to-cart campaigns keep priority, as before.
    if (productIds.length) {
      for (const entry of registered) {
        const trigger = entry.popup.dataset.trigger;
        if (trigger !== 'add_to_cart' && trigger !== 'both') continue;
        if (!isCandidate(entry)) continue;
        let matched = false;
        for (const productId of productIds) {
          if (await popupMatchesAddedProduct(entry.popup, productId)) { matched = true; break; }
        }
        if (matched) {
          scheduleOpen(entry.popup, entry.designMode);
          return;
        }
      }
    }

    // 2. Cart value campaigns open when the cart moves into their range.
    if (cartTotal === null || cartTotal === previousTotal) return;
    for (const entry of registered) {
      if (entry.popup.dataset.trigger !== 'cart_value') continue;
      if (!isCandidate(entry)) continue;
      if (cartInRange(entry.popup, previousTotal)) continue;
      scheduleOpen(entry.popup, entry.designMode);
      return;
    }
  }

  function extractProductId(payload) {
    if (!payload) return null;
    if (Array.isArray(payload.items) && payload.items.length) return payload.items[0].product_id || payload.items[0].productId || null;
    return payload.product_id || payload.productId || null;
  }

  /* ---------- Init ---------- */

  function initRoot(root) {
    if (root.dataset.prohealthPopupInitialised === 'true') return;
    root.dataset.prohealthPopupInitialised = 'true';

    const designMode = root.dataset.designMode === 'true';
    const context = {
      productId: root.dataset.currentProductId && root.dataset.currentProductId !== '0' ? root.dataset.currentProductId : null,
      collectionHandle: root.dataset.currentCollectionHandle || '',
      productCollections: splitList(root.dataset.currentProductCollections)
    };

    const storedTotal = readCents(safeSessionGet(CART_TOTAL_KEY));
    const renderedTotal = readCents(root.dataset.cartTotal);
    if (renderedTotal !== null) setCartTotal(renderedTotal);

    const popups = Array.from(root.querySelectorAll(POPUP_SELECTOR));
    let cartValueScheduled = false;

    popups.forEach((popup) => {
      const entry = { popup, designMode };
      registered.push(entry);

      popup.querySelectorAll('[data-prohealth-popup-close]').forEach((button) => button.addEventListener('click', () => closePopup(popup)));
      const overlay = popup.querySelector('[data-prohealth-popup-overlay]');
      if (overlay && popup.dataset.closeOverlay === 'true') overlay.addEventListener('click', () => closePopup(popup));

      const trigger = popup.dataset.trigger;
      if ((trigger === 'product_visit' || trigger === 'both') && popupHasVisitTarget(popup, context) && isCandidate(entry)) {
        scheduleOpen(popup, designMode);
      }

      // The cart moved into range between page loads (e.g. cart page update without AJAX).
      if (trigger === 'cart_value' && !cartValueScheduled && isCandidate(entry) && !cartInRange(popup, storedTotal)) {
        cartValueScheduled = true;
        scheduleOpen(popup, designMode);
      }
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function unregisterRoot(root) {
    for (let i = registered.length - 1; i >= 0; i -= 1) {
      if (root.contains(registered[i].popup)) registered.splice(i, 1);
    }
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activePopup) closePopup(activePopup);
  });

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
      const match = requestUrl && CART_MUTATION_PATTERN.exec(requestUrl);
      if (match && response.ok) {
        const isAdd = match[1] === 'add';
        response.clone().json().then((payload) => {
          const productId = isAdd ? extractProductId(payload) : null;
          if (productId) document.dispatchEvent(new CustomEvent('prohealth:product-added', { detail: { productId } }));
          const total = payload && typeof payload.total_price === 'number' ? payload.total_price : null;
          queueCartMutation(productId, total);
        }).catch(() => queueCartMutation(null, null));
      }
    } catch (error) {}
    return response;
  };

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (root) unregisterRoot(root);
  });
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('DOMContentLoaded', initAll);
  if (document.readyState !== 'loading') initAll();
})();
